import { ROUTE_META } from "@/seo/route-meta";
import { MONO } from "@/components/home-a/theme";
import { Capture } from "@/components/home-a/StorePage";
import {
  ActionLink, Label, PageHero, Panel, Reveal, RevealGroup, Section, SectionHead, SitePage, useTr, type Tr,
} from "@/components/site/Kit";
import { InlineCta, InlineDemo, InnerCtaBand, InnerHeroCtas } from "@/components/demo/DemoCtas";
import { LeadCapture } from "@/components/site/LeadPage";
import { cn } from "@/lib/utils";

const PATH = "/features/cro-techniques";

type Tech = { title: string; why: string; tactics: string[]; visual?: React.ReactNode };

function ecommerce(tr: Tr): Tech[] {
  return [
    {
      title: tr("Το κουμπί αγοράς κερδίζει την προσοχή", "Make the buy button win attention"),
      why: tr("Αν το Add to cart δεν είναι το δυνατότερο στοιχείο μετά το προϊόν, ο αγοραστής ψάχνει. Η αντίθεση κάνει περισσότερα από το μέγεθος.", "If Add to cart is not the strongest element after the product, shoppers go looking. Contrast does more than size."),
      tactics: [tr("Σκούρο ή έντονο χρώμα, μόνο για το κύριο κουμπί", "A dark or strong colour reserved for the main button"), tr("Λιγότερα badges και banners γύρω του", "Fewer badges and banners around it"), tr("Sticky κουμπί στο κινητό όταν φεύγει από την οθόνη", "A sticky button on mobile once it scrolls away")],
      visual: <Capture scale={0.56} cropTop={712} cropH={88} radius={10} frame={false} variant={{ after: true }} style={{ boxShadow: "0 0 0 1px hsl(var(--border))", maxWidth: "100%" }} />,
    },
    {
      title: tr("Όλο το κόστος πριν από το checkout", "Show the full cost before checkout"),
      why: tr("Τα απρόσμενα έξοδα είναι ο συχνότερος λόγος εγκατάλειψης καλαθιού στην έρευνα του Baymard.", "Unexpected extra costs are the most cited reason for cart abandonment in Baymard's research."),
      tactics: [tr("Κόστος και χρόνος αποστολής στη σελίδα προϊόντος", "Delivery cost and time on the product page"), tr("Όριο δωρεάν αποστολής με μπάρα προόδου", "A free-delivery threshold with a progress bar"), tr("Τελικό σύνολο στο καλάθι", "The final total in the cart")],
    },
    {
      title: tr("Απόδειξη δίπλα στην τιμή", "Proof next to the price"),
      why: tr("Ο αγοραστής διστάζει όταν βλέπει την τιμή. Εκεί πρέπει να βρίσκονται οι κριτικές, όχι σε tab.", "Shoppers hesitate when they see the price. That is where reviews belong, not in a tab."),
      tactics: [tr("Αστέρια και αριθμός κριτικών κάτω από τον τίτλο", "Stars and review count under the title"), tr("Κριτικές με φωτογραφία", "Reviews with photos"), tr("Επιστροφές σε μία γραμμή δίπλα στο κουμπί", "Returns in one line by the button")],
    },
    {
      title: tr("Αγορά ως επισκέπτης", "Guest checkout"),
      why: tr("Ο υποχρεωτικός λογαριασμός είναι από τους πρώτους λόγους εγκατάλειψης. Ζητήστε τον μετά την παραγγελία.", "Forced account creation is one of the top abandonment reasons. Offer it after the order."),
      tactics: [tr("Αγορά χωρίς λογαριασμό από προεπιλογή", "No-account checkout by default"), tr("Express πληρωμές στην κορυφή", "Express payments at the top"), tr("Δημιουργία λογαριασμού στη σελίδα ευχαριστίας", "Account creation on the thank-you page")],
    },
    {
      title: tr("Φόρμα checkout χωρίς τριβή", "A checkout form without friction"),
      why: tr("Κάθε πεδίο είναι ένα σημείο που μπορεί να σταματήσει ο αγοραστής, ειδικά στο κινητό.", "Every field is a place the shopper can stop, especially on a phone."),
      tactics: [tr("Ορατές ετικέτες, όχι μόνο placeholder", "Visible labels, not placeholders only"), tr("Σωστό πληκτρολόγιο και autocomplete", "The right keyboard and autocomplete"), tr("Έλεγχος λαθών τη στιγμή που γράφει", "Inline validation as they type")],
    },
    {
      title: tr("Φίλτρα που ταιριάζουν στον αγοραστή", "Filters that match how shoppers think"),
      why: tr("Η σελίδα κατηγορίας πρέπει να φέρει γρήγορα τον σωστό αγοραστή στο σωστό προϊόν.", "The collection page has to get the right shopper to the right product quickly."),
      tactics: [tr("Μέγεθος, τιμή, χρώμα, χρήση", "Size, price, colour, use"), tr("Τιμή και διαθεσιμότητα σε κάθε κάρτα", "Price and availability on every card"), tr("Προτάσεις όταν η αναζήτηση δεν βρίσκει τίποτα", "Suggestions when search finds nothing")],
    },
    {
      title: tr("Cross-sell κάτω από το κουμπί, όχι πάνω", "Cross-sells below the button, not above"),
      why: tr("Οι προτάσεις ανεβάζουν την αξία παραγγελίας όταν δεν ανταγωνίζονται την ολοκλήρωση.", "Recommendations raise order value when they do not compete with finishing the order."),
      tactics: [tr("Λίγα, σχετικά προϊόντα", "A few relevant items"), tr("Κάτω από το κουμπί checkout", "Below the checkout button"), tr("«Συχνά αγοράζονται μαζί» στη σελίδα προϊόντος", "\"Often bought together\" on the product page")],
    },
    {
      title: tr("Ταχύτητα και σταθερή διάταξη", "Speed and a layout that holds still"),
      why: tr("Ένα widget που φορτώνει αργά και μετακινεί το κουμπί προκαλεί λάθος πάτημα και αποχώρηση.", "A widget that loads late and shifts the button causes a mis-tap and a bounce."),
      tactics: [tr("LCP κάτω από 2.5 s στο κινητό", "LCP under 2.5 s on mobile"), tr("Κρατημένος χώρος για εικόνες και widgets", "Reserved space for images and widgets"), tr("Έλεγχος των apps που δεν αξίζουν πια", "Remove apps that no longer earn their weight")],
    },
    {
      title: tr("Πρώτα το κινητό", "Mobile first, for real"),
      why: tr("Η ίδια σελίδα συμπεριφέρεται αλλιώς στο κινητό: ό,τι είναι δίπλα στο κουμπί στο desktop συχνά είναι τρία scroll πιο κάτω.", "The same page behaves differently on a phone: what sits by the button on desktop is often three scrolls down."),
      tactics: [tr("Κουμπιά τουλάχιστον 44px", "Buttons at least 44px"), tr("Τιμή και κουμπί στην πρώτη οθόνη", "Price and button on the first screen"), tr("Apple Pay και Google Pay", "Apple Pay and Google Pay")],
    },
    {
      title: tr("Αληθινό επείγον", "Urgency that is true"),
      why: tr("Πραγματικό απόθεμα και πραγματική ώρα αποστολής βοηθούν. Μετρητές που μηδενίζουν διδάσκουν δυσπιστία.", "Real stock and a real dispatch cutoff help. Timers that reset teach distrust."),
      tactics: [tr("«Παραγγείλτε ως τις 14:00, φεύγει σήμερα»", "\"Order by 2 pm, ships today\""), tr("Απόθεμα από το σύστημα, όχι σταθερό κείμενο", "Stock from the system, not fixed text"), tr("Προσφορές με πραγματική λήξη", "Offers with a real end date")],
    },
  ];
}

function leadgen(tr: Tr): Tech[] {
  return [
    {
      title: tr("Λιγότερα πεδία", "Fewer form fields"),
      why: tr("Ζητήστε μόνο ό,τι χρειάζεστε για το πρώτο βήμα. Τα υπόλοιπα μπαίνουν στην πρώτη κλήση.", "Ask only for what you need for the first step. The rest belongs in the first call."),
      tactics: [tr("3 έως 5 πεδία για μια προσφορά", "3 to 5 fields for a quote"), tr("Φόρμα σε βήματα όταν χρειάζονται περισσότερα", "A multi-step form when you need more"), tr("Προαιρετικά πεδία σημειωμένα ως προαιρετικά", "Optional fields marked optional")],
      visual: <LeadCapture kind="mobile" scale={0.56} cropTop={262} cropH={470} radius={10} frame={false} variant={{ after: true }} style={{ boxShadow: "0 0 0 1px hsl(var(--border))" }} />,
    },
    {
      title: tr("Ένας στόχος ανά landing page", "One goal per landing page"),
      why: tr("Κάθε επιπλέον σύνδεσμος είναι μια έξοδος. Η σελίδα καμπάνιας έχει ένα κουμπί.", "Every extra link is an exit. A campaign page has one button."),
      tactics: [tr("Χωρίς κεντρικό μενού", "No main navigation"), tr("Το μήνυμα της διαφήμισης στην επικεφαλίδα", "The ad's promise in the headline"), tr("Ένα CTA που επαναλαμβάνεται", "One CTA, repeated")],
    },
    {
      title: tr("Κουμπί που λέει τι παίρνετε", "A button that says what you get"),
      why: tr("«Submit» δεν απαντά στο γιατί. «Πάρτε την προσφορά σας» απαντά.", "\"Submit\" does not answer why. \"Get my free quote\" does."),
      tactics: [tr("Ρήμα και αποτέλεσμα", "A verb and an outcome"), tr("Υψηλή αντίθεση", "High contrast"), tr("Μια γραμμή κάτω: τι γίνεται μετά", "One line below: what happens next")],
    },
    {
      title: tr("Επικεφαλίδα με το ποιος και το τι", "A headline that says who and what"),
      why: tr("Σε πέντε δευτερόλεπτα ο επισκέπτης πρέπει να ξέρει αν είναι στο σωστό μέρος.", "In five seconds a visitor should know whether they are in the right place."),
      tactics: [tr("Τι κάνετε, για ποιον, με ποιο αποτέλεσμα", "What you do, for whom, with what result"), tr("Χωρίς «καινοτόμες λύσεις»", "No \"innovative solutions\""), tr("Υπότιτλος με μια απόδειξη", "A subheadline with one proof point")],
    },
    {
      title: tr("Απόδειξη δίπλα στη φόρμα", "Proof next to the form"),
      why: tr("Η αμφιβολία εμφανίζεται τη στιγμή που ζητάτε στοιχεία. Εκεί χρειάζεται η απόδειξη.", "Doubt appears the moment you ask for details. That is where proof is needed."),
      tactics: [tr("Μία κριτική πελάτη πάνω από το κουμπί", "One client review above the button"), tr("Αριθμοί που ελέγχονται", "Numbers that can be checked"), tr("Λογότυπα που αναγνωρίζει το κοινό σας", "Logos your audience recognises")],
    },
    {
      title: tr("Lead magnet που λύνει ένα πρόβλημα", "A lead magnet that solves one problem"),
      why: tr("Ένα εργαλείο, ένας υπολογισμός ή μια λίστα που χρησιμοποιείται σήμερα αξίζει περισσότερο από ένα γενικό e-book.", "A tool, a calculator or a checklist people use today is worth more than a generic e-book."),
      tactics: [tr("Συγκεκριμένος τίτλος και όφελος", "A specific title and benefit"), tr("Μόνο email για να το πάρουν", "Email only to get it"), tr("Άμεση παράδοση", "Instant delivery")],
    },
    {
      title: tr("Τι γίνεται μετά την υποβολή", "Say what happens after they submit"),
      why: tr("Η αβεβαιότητα για το επόμενο βήμα κρατάει πίσω το κλικ.", "Uncertainty about the next step holds back the click."),
      tactics: [tr("«Απάντηση μέσα σε μία εργάσιμη»", "\"Reply within one business day\""), tr("Ποιος θα επικοινωνήσει", "Who will get in touch"), tr("Χωρίς δέσμευση, αν ισχύει", "No commitment, if true")],
    },
    {
      title: tr("Σελίδα ευχαριστίας που δουλεύει", "A thank-you page that works"),
      why: tr("Ο επισκέπτης μόλις είπε ναι. Είναι η καλύτερη στιγμή για το επόμενο βήμα.", "The visitor just said yes. It is the best moment for the next step."),
      tactics: [tr("Κλείστε ραντεβού εκεί", "Book the call right there"), tr("Ένα χρήσιμο υλικό", "One useful resource"), tr("Ερώτηση: πώς μας βρήκατε", "Ask how they found you here, not in the form")],
    },
    {
      title: tr("Σελίδες υπηρεσιών με αποτελέσματα", "Service pages that show outcomes"),
      why: tr("Ο επισκέπτης αγοράζει το αποτέλεσμα, όχι τη λίστα με τις υπηρεσίες.", "Visitors buy the outcome, not the list of services."),
      tactics: [tr("Για ποιον είναι και για ποιον όχι", "Who it is for, and who it is not for"), tr("Ένα case study με αριθμούς", "One case study with numbers"), tr("Εύρος τιμής όπου γίνεται", "A price range where possible")],
    },
    {
      title: tr("Τεστ όταν υπάρχει traffic, αλλαγές όταν δεν υπάρχει", "Test when you have traffic, ship when you do not"),
      why: tr("Τα περισσότερα sites δεν έχουν αρκετές μετατροπές για στατιστικά σημαντικό τεστ σε κάθε αλλαγή.", "Most sites do not have enough conversions for a significant test on every change."),
      tactics: [tr("Τεστ στις σελίδες με τον περισσότερο όγκο", "Test on the pages with the most volume"), tr("Αλλού: αλλαγή και παρακολούθηση τάσης", "Elsewhere: ship and watch the trend"), tr("Μία υπόθεση ανά τεστ", "One hypothesis per test")],
    },
  ];
}

function TechList({ items, offset = 0 }: { items: Tech[]; offset?: number }) {
  return (
    <RevealGroup className="mt-12 grid grid-cols-1 gap-3.5 md:grid-cols-2" stagger={0.04}>
      {items.map((t, i) => (
        <Reveal key={t.title}>
          <Panel lift className="flex flex-col p-6">
            <div className="flex items-start gap-4">
              <span className="text-[26px] font-medium leading-none tracking-[-0.03em] text-[hsl(var(--ec-mint))]" style={{ fontFamily: MONO }}>{String(i + 1 + offset).padStart(2, "0")}</span>
              <div>
                <h3 className="text-[19px] font-semibold tracking-[-0.015em]">{t.title}</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">{t.why}</p>
              </div>
            </div>
            <ul className="mt-4 flex flex-wrap gap-1.5 pl-[46px]">
              {t.tactics.map((x) => (
                <li key={x} className="rounded-full border border-border bg-background/50 px-3 py-1 text-[12.5px] text-foreground/90">{x}</li>
              ))}
            </ul>
            {t.visual && <div className="mt-5 flex justify-center pl-[46px]">{t.visual}</div>}
          </Panel>
        </Reveal>
      ))}
    </RevealGroup>
  );
}

export default function CROTechniques() {
  const { tr, isEl } = useTr();
  const meta = ROUTE_META[PATH];

  const frameworks = [
    { name: "LIFT", body: tr("Έξι παράγοντες: πρόταση αξίας, συνάφεια, σαφήνεια, άγχος, απόσπαση, επείγον.", "Six factors: value proposition, relevance, clarity, anxiety, distraction, urgency."), factors: ["Value", "Relevance", "Clarity", "Anxiety", "Distraction", "Urgency"] },
    { name: "PIE", body: tr("Ιεράρχηση ιδεών με βάση δυναμική, σημασία και ευκολία.", "Prioritise ideas by potential, importance and ease."), factors: ["Potential", "Importance", "Ease"] },
    { name: "ICE", body: tr("Γρήγορη βαθμολογία για πολλές ιδέες: επίδραση, βεβαιότητα, ευκολία.", "A quick score for many ideas: impact, confidence, ease."), factors: ["Impact", "Confidence", "Ease"] },
  ];

  return (
    <SitePage related="features/cro-techniques">
      <PageHero
        crumbs={[{ label: tr("Αρχική", "Home"), to: "/" }, { label: tr("Οδηγοί", "Guides") }, { label: tr("Τεχνικές CRO", "CRO techniques") }]}
        kicker={tr("Τεχνικές CRO", "CRO techniques")}
        title={isEl ? "20 τεχνικές CRO που αυξάνουν τις μετατροπές" : meta.h1}
        lead={
          isEl
            ? "Δέκα για e-shops, δέκα για lead generation, και τα frameworks για να αποφασίσετε από πού να ξεκινήσετε. Κάθε τεχνική με το γιατί δουλεύει και πώς εφαρμόζεται."
            : meta.intro
        }
      >
        <InnerHeroCtas kind="cro" center />
      </PageHero>

      <Section id="ecommerce">
        <SectionHead
          idx="01"
          label="E-commerce"
          title={tr("10 τεχνικές για e-shops", "10 techniques for online stores")}
          lead={tr("Από τη σελίδα προϊόντος ως το checkout, με τη σειρά που συνήθως αξίζει να τις δοκιμάσετε.", "From the product page to checkout, roughly in the order they are worth trying.")}
        />
        <TechList items={ecommerce(tr)} />
      </Section>

      <Section id="lead-generation">
      <InlineDemo
        kind="cro"
        kicker={tr("Δείγμα report", "Sample report")}
        title={tr("Δείτε ποιες τεχνικές λείπουν από μια πραγματική σελίδα", "See which techniques a real page is missing")}
        body={tr("Στο demo κατάστημα KYMA το report βρίσκει ποιες από αυτές τις τεχνικές λείπουν (βαθμολογία στο σημείο απόφασης, υπόσχεση παράδοσης, πρόοδος προς δωρεάν αποστολή) και τις βάζει σε σειρά επίδρασης.", "On the KYMA demo store the report finds which of these techniques are missing (a rating at the decision point, a delivery promise, free-delivery progress) and ranks them by impact.")}
      />

        <SectionHead
          idx="02"
          label="Lead generation"
          title={tr("10 τεχνικές για lead generation", "10 techniques for lead generation")}
          lead={tr("Για landing pages, φόρμες και σελίδες υπηρεσιών.", "For landing pages, forms and service pages.")}
        />
        <TechList items={leadgen(tr)} offset={10} />
      </Section>

      <Section>
        <SectionHead
          idx="03"
          label={tr("Frameworks", "Frameworks")}
          title={tr("Πώς να διαλέξετε από πού να ξεκινήσετε", "How to choose where to start")}
          lead={tr("Είκοσι τεχνικές είναι πολλές. Αυτά τα frameworks βάζουν σειρά.", "Twenty techniques are a lot. These frameworks put them in order.")}
        />
        <RevealGroup className="mt-12 grid grid-cols-1 gap-3.5 md:grid-cols-3" stagger={0.08}>
          {frameworks.map((f) => (
            <Reveal key={f.name}>
              <Panel lift className="p-6">
                <p className="text-[34px] font-semibold tracking-[-0.03em] text-[hsl(var(--ec-mint))]">{f.name}</p>
                <p className="mt-2 text-[14.5px] leading-relaxed text-[hsl(var(--ec-text-2))]">{f.body}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {f.factors.map((x) => <span key={x} className="rounded-md border border-border px-2 py-1 text-[11.5px]" style={{ fontFamily: MONO }}>{x}</span>)}
                </div>
              </Panel>
            </Reveal>
          ))}
        </RevealGroup>
        <Reveal className="mt-3.5">
          <Panel className={cn("grid grid-cols-1 items-center gap-5 p-6 sm:p-8 md:grid-cols-[minmax(0,1fr)_auto]")}>
            <div>
              <Label className="text-[hsl(var(--ec-mint))]">{tr("Ή αφήστε την ανάλυση να βάλει σειρά", "Or let the audit set the order")}</Label>
              <p className="mt-2 text-[16px] leading-relaxed">
                {tr(
                  "Το EyeCaptain ελέγχει τη σελίδα σας για αυτές τις τεχνικές και σας δίνει μόνο όσες λείπουν, με σειρά αναμενόμενης επίδρασης.",
                  "EyeCaptain checks your page against these techniques and gives you only the ones that are missing, ranked by expected impact.",
                )}
              </p>
            </div>
            <ActionLink to="/pricing">{tr("Δείτε τα πλάνα", "See the plans")}</ActionLink>
          </Panel>
        </Reveal>
      </Section>

      <InnerCtaBand
        kind="cro"
        title={tr("Δείτε ποιες τεχνικές λείπουν από τη σελίδα σας", "See which techniques your page is missing")}
        body={tr("Το EyeCaptain ελέγχει τη σελίδα σας για αυτές τις τεχνικές και σας δίνει μόνο όσες λείπουν. Διαλέξτε πλάνο, ή δείτε πρώτα ένα δείγμα.", "EyeCaptain checks your page for these techniques and gives you only the missing ones. Pick a plan, or see a sample first.")}
      />
    </SitePage>
  );
}
